// frontend/src/components/BookReading/ChapterPreview.js
//#region Imports
import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { Editor } from 'react-draft-wysiwyg';
import { EditorState, ContentState } from 'draft-js';
import '../../styles/App.css';
//#endregion

//#region Componente Principal
function ChapterPreview({
  refs,
  currentPage,
  setCurrentPage,
  editorState,
  totalPages,
  setTotalPages,
  t
}) {
  //#region Declarações
  const [paginationData, setPaginationData] = useState([]);
  const [scrollMode] = useState(true);
  const [isCalculatingPagination, setIsCalculatingPagination] = useState(true);
  const [fontSizeLevel, setFontSizeLevel] = useState('small');

  const scrollContainerRef = useRef(null);
  const pageRefs = useRef([]);
  const previewContainerRef = useRef(null);
  const blockHeightCache = useRef(new Map());
  const measureContainerRef = useRef(null);
  const paginationCache = useRef(new Map());

  const PAGE_HEIGHT = 1200;
  const PAGE_WIDTH = 800;
  // const FONT_FAMILY = 'Open Sans, Arial, sans-serif';
  const ADDITIONAL_MARGIN = 10;
  const LINE_HEIGHT = 1;
  const IMAGE_HEIGHT = 200;
  const MIN_BLOCK_HEIGHT = 30;
  const MIN_RETURN_HEIGHT = 20;

  const FONT_SIZE = 16;

  const FONT_SIZE_STYLE_MULTIPLY = useMemo(() => ({
    headerOne: 1.5,
    headerTwo: 1.4,
    headerThree: 1.3,
    headerFour: 1.2,
    headerFive: 1.1,
    headerSix: 1.0,
    blockquote: 1.0,
    code: 1.0,
    unstyled: 1.0
  }), []);

  const FONT_SIZES_MULTIPLY = useMemo(() => ({
    small: 1,
    medium: 1.2,
    large: 1.5
  }), []);
  //#endregion

  //#region Style injection
  // Injeta CSS dinâmico
  useEffect(() => {
    const styleId = 'chapter-preview-dynamic-styles';
    let styleTag = document.getElementById(styleId);

    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = styleId;
      document.head.appendChild(styleTag);
    }

    const multiplier = FONT_SIZES_MULTIPLY[fontSizeLevel];

    styleTag.textContent = `
      .preview-editor > div > div > div > div > * > * > * > span {
        font-size: ${multiplier * 100}% !important;
      }
    `;
  }, [fontSizeLevel, FONT_SIZES_MULTIPLY]);
  //#endregion

  //#region Custom Block Renderer (apenas para imagens e listas)
  const customBlockRenderer = useCallback((contentBlock) => {
    const blockType = contentBlock.getType();

    // Apenas para blocos atômicos (imagens)
    if (blockType === 'atomic') {
      const entityKey = contentBlock.getEntityAt(0);
      if (entityKey && editorState) {
        const entity = editorState.getCurrentContent().getEntity(entityKey);
        if (entity.getType() === 'IMAGE') {
          const { src } = entity.getData();
          const webpSrc = src.replace(/(png|jpeg);base64/, 'webp;base64');
          return {
            component: () => (
              <div style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                margin: '10px 0'
              }}>
                <img src={webpSrc} alt="uploaded" style={{ maxWidth: '100%', height: 'auto' }} />
              </div>
            ),
            editable: false,
          };
        }
      }
    }

    return null;
  }, [editorState]);
  //#endregion

  //#region Funções de Medição Otimizadas
  const analyzeImageEntity = useCallback((entityData) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = function () {
        const result = {
          src: entityData.getData().src,
          originalWidth: this.naturalWidth,
          originalHeight: this.naturalHeight,
          aspectRatio: this.naturalWidth / this.naturalHeight,
          fileSize: Math.round((entityData.getData().src.length * 0.75) / 1024) + ' KB'
        };
        resolve(result);
      };
      img.onerror = function () {
        console.error('Erro ao carregar a imagem:', entityData.getData().src);
        resolve({ originalHeight: IMAGE_HEIGHT, originalWidth: PAGE_WIDTH, aspectRatio: 1 });
      };
      img.src = entityData.getData().src;
    });
  }, []);

  const measureBlockHeight = useCallback(async (block, contentState) => {
    const cacheKey = `${block.key}-${fontSizeLevel}`;
    if (blockHeightCache.current.has(cacheKey)) {
      return blockHeightCache.current.get(cacheKey);
    }

    if (!measureContainerRef.current) return MIN_BLOCK_HEIGHT;

    const currentFontSize = FONT_SIZE;
    const multiplier = FONT_SIZES_MULTIPLY[fontSizeLevel];

    // Para blocos atômicos (imagens)
    if (block.type === 'atomic') {
      const entityKey = block.getEntityAt(0);
      if (entityKey) {
        try {
          const entity = contentState.getEntity(entityKey);
          if (entity.getType() === 'IMAGE') {
            const result = await analyzeImageEntity(entity);
            const scaledHeight = Math.min(
              result.originalHeight,
              PAGE_HEIGHT - 100
            );
            const finalHeight = scaledHeight + 20;
            blockHeightCache.current.set(cacheKey, finalHeight);
            return finalHeight;
          }
        } catch (error) {
          console.error('Error analyzing image entity:', error);
          const height = IMAGE_HEIGHT + 20;
          blockHeightCache.current.set(cacheKey, height);
          return height;
        }
      }
    }

    // Medição rápida sem DOM para outros blocos
    const fontSizeMap = {
      'header-one': currentFontSize * FONT_SIZE_STYLE_MULTIPLY.headerOne * multiplier,
      'header-two': currentFontSize * FONT_SIZE_STYLE_MULTIPLY.headerTwo * multiplier,
      'header-three': currentFontSize * FONT_SIZE_STYLE_MULTIPLY.headerThree * multiplier,
      'header-four': currentFontSize * FONT_SIZE_STYLE_MULTIPLY.headerFour * multiplier,
      'header-five': currentFontSize * FONT_SIZE_STYLE_MULTIPLY.headerFive * multiplier,
      'header-six': currentFontSize * FONT_SIZE_STYLE_MULTIPLY.headerSix * multiplier,
      'blockquote': currentFontSize * FONT_SIZE_STYLE_MULTIPLY.blockquote * multiplier,
      'code': currentFontSize * FONT_SIZE_STYLE_MULTIPLY.code * multiplier,
      'unstyled': currentFontSize * FONT_SIZE_STYLE_MULTIPLY.unstyled * multiplier
    };

    const fontSize = fontSizeMap[block.type] || currentFontSize;
    const text = block.text || ' ';
    const lines = Math.ceil(text.length * fontSize * 0.6 / PAGE_WIDTH); // Estimativa de linhas
    const height = Math.max(lines * fontSize * LINE_HEIGHT + ADDITIONAL_MARGIN, MIN_RETURN_HEIGHT);

    blockHeightCache.current.set(cacheKey, height);
    return height;
  }, [fontSizeLevel, FONT_SIZE_STYLE_MULTIPLY, FONT_SIZES_MULTIPLY, analyzeImageEntity]);
  //#endregion

  //#region Paginação Otimizada
  const memoizedBlocks = useMemo(() =>
    editorState.getCurrentContent().getBlocksAsArray(),
    [editorState]
  );

  const calculatePagination = useCallback(async () => {
    const cacheKey = `${editorState.getCurrentContent().getBlockMap().size}-${fontSizeLevel}`;

    if (paginationCache.current.has(cacheKey)) {
      return paginationCache.current.get(cacheKey);
    }

    const blocks = memoizedBlocks;
    const contentState = editorState.getCurrentContent();
    const pages = [];
    let currentPageBlocks = [];
    let currentPageHeight = 0;

    // Batch de medições para melhor performance
    const blockHeights = await Promise.all(
      blocks.map(block => measureBlockHeight(block, contentState))
    );

    for (let index = 0; index < blocks.length; index++) {
      const blockHeight = blockHeights[index];

      if (currentPageHeight + blockHeight > PAGE_HEIGHT && currentPageBlocks.length > 0) {
        pages.push([...currentPageBlocks]);
        currentPageBlocks = [index];
        currentPageHeight = blockHeight;
      } else {
        currentPageBlocks.push(index);
        currentPageHeight += blockHeight;
      }
    }

    if (currentPageBlocks.length > 0) {
      pages.push(currentPageBlocks);
    }

    paginationCache.current.set(cacheKey, pages);
    return pages;
  }, [measureBlockHeight, fontSizeLevel, editorState, memoizedBlocks]);
  //#endregion

  //#region Renderização
  const renderFullContent = useCallback(() => {
    if (!paginationData.length) return null;

    const blocks = editorState.getCurrentContent().getBlocksAsArray();
    const multiplier = FONT_SIZES_MULTIPLY[fontSizeLevel];

    return paginationData.map((pageBlockIndices, pageIndex) => {
      const pageBlocks = pageBlockIndices.map(index => blocks[index]).filter(Boolean);

      if (pageBlocks.length === 0) return null;

      try {
        const pageContentState = ContentState.createFromBlockArray(pageBlocks);
        const pageEditorState = EditorState.createWithContent(pageContentState);

        return (
          <div
            key={pageIndex}
            ref={el => pageRefs.current[pageIndex] = el}
            className="page-section"
            style={{
              minHeight: `${PAGE_HEIGHT}px`,
              borderBottom: '2px dashed #ccc',
              padding: '30px 0px',
              position: 'relative'
            }}
          >
            <div
              style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                background: 'rgba(0,0,0,0.1)',
                padding: '5px 10px',
                borderRadius: '5px',
                fontSize: `${multiplier * 12}px`,
                color: '#666',
                zIndex: 100
              }}>
              {t.page || 'Página'} {pageIndex + 1}
            </div>
            <Editor
              editorState={pageEditorState}
              readOnly={true}
              toolbarHidden={true}
              editorClassName="preview-editor"
              customBlockRenderFunc={customBlockRenderer}
            />
          </div>
        );
      } catch (error) {
        console.error('Erro ao criar página:', error);
        return null;
      }
    });
  }, [paginationData, editorState, customBlockRenderer, t, fontSizeLevel, FONT_SIZES_MULTIPLY]);
  //#endregion

  //#region Efeitos
  useEffect(() => {
    const updatePagination = async () => {
      setIsCalculatingPagination(true);
      try {
        const newPaginationData = await calculatePagination();
        setPaginationData(newPaginationData);
        setTotalPages(newPaginationData.length);
      } finally {
        setIsCalculatingPagination(false);
      }
    };

    updatePagination();
  }, [editorState, calculatePagination, setTotalPages, fontSizeLevel]);

  useEffect(() => {
    blockHeightCache.current.clear();
    paginationCache.current.clear();
  }, [editorState, fontSizeLevel]);

  useEffect(() => {
    if (!scrollMode && previewContainerRef.current) {
      previewContainerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'start'
      });
    }
  }, [scrollMode]);
  //#endregion

  //#region Renderização Final
  return (
    <>
      <div ref={measureContainerRef} style={{ position: 'absolute', visibility: 'hidden' }} />

      <div className="d-flex justify-content-end align-items-center mb-2 gap-2 mt-2 me-1">
        <span style={{ fontSize: '20px', color: '#666' }}>{t.fontSize}</span>
        {['small', 'medium', 'large'].map(level => (
          <button
            key={level}
            onClick={() => setFontSizeLevel(level)}
            className={`btn btn-sm ${fontSizeLevel === level ? 'btn-primary' : 'btn-outline-secondary'}`}
            style={{
              fontSize: level === 'small' ? '11px' : level === 'medium' ? '13px' : '16px',
              padding: '2px 8px',
              minWidth: '32px'
            }}
            title={level === 'small' ? 'Pequena' : level === 'medium' ? 'Média' : 'Grande'}
          >
            {level === 'small' ? 'P' : level === 'medium' ? 'M' : 'G'}
          </button>
        ))}
      </div>

      <div className="border rounded mt-3">
        {!scrollMode && <h4 ref={previewContainerRef} className='p-2'>{t.pagePreview} {currentPage}:</h4>}

        <div
          ref={scrollContainerRef}
          className='preview-container'
          style={{
            maxHeight: scrollMode ? '800px' : '100%',
            overflowY: scrollMode ? 'scroll' : 'auto',
            position: 'relative'
          }}
        >
          {isCalculatingPagination ? (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              Calculando paginação...
            </div>
          ) : (
            <div ref={refs}>
              {renderFullContent()}
            </div>
          )}
        </div>
      </div>
    </>
  );
  //#endregion
}

export default React.memo(ChapterPreview, (prevProps, nextProps) => {
  // Só re-renderiza se estas props mudarem
  return (
    prevProps.editorState === nextProps.editorState &&
    prevProps.currentPage === nextProps.currentPage &&
    prevProps.totalPages === nextProps.totalPages &&
    prevProps.t === nextProps.t
  );
});